import { useRowTransitions } from "@/hooks/useRowTransitions";
import { ReactNode, useRef } from "react";

/**
 * When the list's rows change. `version` must change exactly when the keyed
 * rows do; a new `resetKey` is a different list altogether and never
 * animates. See useRowTransitions.
 */
export type RowTransition = { version: string; resetKey: string | number };

/**
 * A wrapper whose `[data-row-key]` children animate between renders: rows
 * that stay glide to their new spot, new ones fade up in, removed ones fade
 * out where they were. Text rows, chips and icons alike.
 */
export const AnimatedList = ({
  transition,
  className,
  children,
}: {
  transition: RowTransition;
  className?: string;
  children: ReactNode;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const ghostLayerRef = useRef<HTMLDivElement>(null);
  useRowTransitions({ wrapperRef, ghostLayerRef, ...transition });
  return (
    <div ref={wrapperRef} className="relative">
      <div className={className}>{children}</div>
      <div
        ref={ghostLayerRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
      />
    </div>
  );
};
